/**
 * API client for Numuw Counter Cloud POS backend.
 */

const TOKEN_KEY = 'cashier_jwt';
const ACCOUNT_NO_KEY = 'cashier_account_no';

class APIService {
  constructor() {
    this.baseURL = null;
  }

  setConfig(config) {
    if (!config || !config.apiUrl) {
      throw new Error('Invalid config: apiUrl required');
    }
    this.baseURL = config.apiUrl.replace(/\/$/, '');
  }

  getBaseURL() {
    return this.baseURL;
  }

  getAccountNo() {
    try {
      return localStorage.getItem(ACCOUNT_NO_KEY) || '';
    } catch {
      return '';
    }
  }

  setAccountNo(accountNo) {
    try {
      if (accountNo) localStorage.setItem(ACCOUNT_NO_KEY, accountNo);
      else localStorage.removeItem(ACCOUNT_NO_KEY);
    } catch (_) {}
  }

  getToken() {
    return sessionStorage.getItem(TOKEN_KEY);
  }

  setToken(token) {
    if (token) sessionStorage.setItem(TOKEN_KEY, token);
    else sessionStorage.removeItem(TOKEN_KEY);
    try { localStorage.removeItem(TOKEN_KEY); } catch (_) {}
  }

  async request(method, endpoint, data = null, options = {}) {
    if (!this.baseURL) throw new Error('API not configured. Set config first.');
    const token = this.getToken();
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const config = { method, headers, credentials: 'include' };
    if (data && (method === 'POST' || method === 'PUT' || method === 'PATCH')) {
      config.body = JSON.stringify(data);
    }

    const response = await fetch(`${this.baseURL}${endpoint}`, config);
    const text = await response.text();
    let result;
    try {
      result = text ? JSON.parse(text) : {};
    } catch {
      result = { error: text || 'Invalid response' };
    }

    if (!response.ok) {
      const err = new Error(result.error || `HTTP ${response.status}`);
      err.status = response.status;
      err.body = result;
      throw err;
    }
    return result;
  }

  async get(endpoint) {
    return this.request('GET', endpoint);
  }

  async post(endpoint, data) {
    return this.request('POST', endpoint, data);
  }

  async put(endpoint, data) {
    return this.request('PUT', endpoint, data);
  }

  async delete(endpoint) {
    return this.request('DELETE', endpoint);
  }

  async login(accountNo, username, password) {
    const res = await this.post('/api/auth/login', {
      account_no: accountNo,
      username,
      password,
    });
    if (res.token) this.setToken(res.token);
    if (accountNo) this.setAccountNo(String(accountNo).trim().toUpperCase());
    return res;
  }

  async logout() {
    try {
      if (this.baseURL) {
        await fetch(`${this.baseURL}/api/auth/logout`, {
          method: 'POST',
          credentials: 'include',
        });
      }
    } catch (_) {}
    this.setToken(null);
  }

  async getMe() {
    return this.get('/api/auth/me');
  }

  async getStoreStatus() {
    return this.get('/api/auth/store-status');
  }

  async uploadImage(imageData, fileName, subdir = 'products') {
    return this.post('/api/uploads/image', { imageData, fileName, subdir });
  }
  async getCurrentSession() {
    return this.get('/api/sessions/current');
  }

  async openSession(body = {}) {
    return this.post('/api/sessions/open', body);
  }

  async closeSession(body) {
    return this.post('/api/sessions/close', body);
  }

  async forceCloseSession(body) {
    return this.post('/api/sessions/force-close', body);
  }

  // Admin sessions
  async getSessions(params = {}) {
    const filtered = Object.fromEntries(
      Object.entries(params).filter(([, v]) => v != null && v !== '')
    );
    const sp = new URLSearchParams(filtered).toString();
    return this.get(`/api/sessions${sp ? '?' + sp : ''}`);
  }

  async reviewSession(id, data) {
    return this.post(`/api/sessions/${id}/review`, data);
  }

  async forceCloseSessionById(id, data) {
    return this.post(`/api/sessions/${id}/force-close`, data);
  }

  // Products
  async getProducts(categoryId) {
    const q = categoryId ? `?category_id=${categoryId}` : '';
    return this.get(`/api/products${q}`);
  }

  async getProduct(id) {
    return this.get(`/api/products/${id}`);
  }

  async createProduct(data) {
    return this.post('/api/products', data);
  }

  async updateProduct(id, data) {
    return this.put(`/api/products/${id}`, data);
  }

  async deleteProduct(id) {
    return this.delete(`/api/products/${id}`);
  }

  // Offers
  async getOffers(activeOnly) {
    const q = activeOnly ? '?active_only=1' : '';
    return this.get(`/api/offers${q}`);
  }

  async getOffer(id) {
    return this.get(`/api/offers/${id}`);
  }

  async createOffer(data) {
    return this.post('/api/offers', data);
  }

  async updateOffer(id, data) {
    return this.put(`/api/offers/${id}`, data);
  }

  async deleteOffer(id) {
    return this.delete(`/api/offers/${id}`);
  }

  // Categories
  async getCategories() {
    return this.get('/api/categories');
  }

  async createCategory(data) {
    return this.post('/api/categories', data);
  }

  async updateCategory(id, data) {
    return this.put(`/api/categories/${id}`, data);
  }

  async deleteCategory(id) {
    return this.delete(`/api/categories/${id}`);
  }

  // Orders
  async getOrders(params = {}) {
    const filtered = Object.fromEntries(
      Object.entries(params).filter(([, v]) => v != null && v !== '' && v !== 'all')
    );
    const sp = new URLSearchParams(filtered).toString();
    return this.get(`/api/orders${sp ? '?' + sp : ''}`);
  }

  async getOrder(id) {
    return this.get(`/api/orders/${id}`);
  }

  async createOrder(order) {
    return this.post('/api/orders', order);
  }

  // Refunds
  async getOrderByReceipt(receiptNum) {
    return this.get(`/api/orders/by-receipt/${encodeURIComponent(receiptNum)}`);
  }

  async createCashierRefund(data) {
    return this.post('/api/refunds/cashier-initiated', data);
  }

  async getRefunds(params = {}) {
    const filtered = Object.fromEntries(
      Object.entries(params).filter(([, v]) => v != null && v !== '' && v !== 'all')
    );
    const sp = new URLSearchParams(filtered).toString();
    return this.get(`/api/refunds${sp ? '?' + sp : ''}`);
  }

  async getRefund(id) {
    return this.get(`/api/refunds/${id}`);
  }

  // Sales (product sold quantities)
  async getProductSales(params = {}) {
    const filtered = Object.fromEntries(
      Object.entries(params).filter(([, v]) => v != null && v !== '' && v !== 'all')
    );
    const sp = new URLSearchParams(filtered).toString();
    return this.get(`/api/sales/by-product${sp ? '?' + sp : ''}`);
  }

  async getSalesTimeSeries(params = {}) {
    const filtered = Object.fromEntries(
      Object.entries(params).filter(([, v]) => v != null && v !== '' && v !== 'all')
    );
    const sp = new URLSearchParams(filtered).toString();
    return this.get(`/api/sales/timeseries${sp ? '?' + sp : ''}`);
  }

  async getOfferSales(params = {}) {
    const filtered = Object.fromEntries(
      Object.entries(params).filter(([, v]) => v != null && v !== '' && v !== 'all')
    );
    const sp = new URLSearchParams(filtered).toString();
    return this.get(`/api/sales/by-offer${sp ? '?' + sp : ''}`);
  }

  // Customers
  async getCustomers(q, withStats = false) {
    const params = new URLSearchParams();
    if (q && q.trim()) params.set('q', q.trim());
    if (withStats) params.set('withStats', '1');
    const qs = params.toString();
    return this.get(`/api/customers${qs ? '?' + qs : ''}`);
  }

  async getCustomer(id) {
    return this.get(`/api/customers/${id}`);
  }

  async createCustomer(data) {
    return this.post('/api/customers', data);
  }

  async updateCustomer(id, data) {
    return this.put(`/api/customers/${id}`, data);
  }

  async deleteCustomer(id) {
    return this.delete(`/api/customers/${id}`);
  }

  async upsertCustomer(data) {
    return this.post('/api/customers/upsert', data);
  }

  // Registers

  // Users
  async getUsers(includeInactive = false) {
    return this.get(`/api/users?includeInactive=${includeInactive}`);
  }

  async createUser(data) {
    return this.post('/api/users', data);
  }

  async updateUser(id, data) {
    return this.put(`/api/users/${id}`, data);
  }

  async deleteUser(id) {
    return this.delete(`/api/users/${id}`);
  }

  async renewRefundCode(id) {
    return this.post(`/api/users/${id}/renew-refund-code`);
  }

  // Store
  async getStore() {
    return this.get('/api/store');
  }

  async updateStore(data) {
    return this.put('/api/store', data);
  }


  // Expenses
  async getExpenseCategories() {
    return this.get('/api/expenses/categories');
  }

  async getExpenses(params = {}) {
    const filtered = Object.fromEntries(
      Object.entries(params).filter(([, v]) => v != null && v !== '' && v !== 'all')
    );
    const sp = new URLSearchParams(filtered).toString();
    return this.get(`/api/expenses${sp ? '?' + sp : ''}`);
  }

  async createExpense(data) {
    return this.post('/api/expenses', data);
  }

  async updateExpense(id, data) {
    return this.put(`/api/expenses/${id}`, data);
  }

  async deleteExpense(id) {
    return this.delete(`/api/expenses/${id}`);
  }

  async createExpenseCategory(data) {
    return this.post('/api/expenses/categories', data);
  }

  async updateExpenseCategory(id, data) {
    return this.put(`/api/expenses/categories/${id}`, data);
  }

  async deleteExpenseCategory(id) {
    return this.delete(`/api/expenses/categories/${id}`);
  }

  // Suppliers
  async getSuppliers() {
    return this.get('/api/suppliers');
  }

  async createSupplier(data) {
    return this.post('/api/suppliers', data);
  }

  async updateSupplier(id, data) {
    return this.put(`/api/suppliers/${id}`, data);
  }

  async deleteSupplier(id) {
    return this.delete(`/api/suppliers/${id}`);
  }

  // Delivery persons
  async getDeliveryPersons() {
    return this.get('/api/delivery-persons');
  }

  async createDeliveryPerson(data) {
    return this.post('/api/delivery-persons', data);
  }

  async updateDeliveryPerson(id, data) {
    return this.put(`/api/delivery-persons/${id}`, data);
  }

  async deleteDeliveryPerson(id) {
    return this.delete(`/api/delivery-persons/${id}`);
  }

  // Purchases
  async getPurchases(params = {}) {
    const filtered = Object.fromEntries(
      Object.entries(params).filter(([, v]) => v != null && v !== '' && v !== 'all')
    );
    const sp = new URLSearchParams(filtered).toString();
    return this.get(`/api/purchases${sp ? '?' + sp : ''}`);
  }

  async createPurchase(data) {
    return this.post('/api/purchases', data);
  }

  async updatePurchase(id, data) {
    return this.put(`/api/purchases/${id}`, data);
  }

  async deletePurchase(id) {
    return this.delete(`/api/purchases/${id}`);
  }

  // Monthly closings
  async getMonthlyClosings() {
    return this.get('/api/monthly-closings');
  }

  async createMonthlyClosing(closing_dateOrPayload) {
    const payload =
      typeof closing_dateOrPayload === 'string' || closing_dateOrPayload == null
        ? { closing_date: closing_dateOrPayload }
        : closing_dateOrPayload;
    const body = { closing_date: payload.closing_date };
    const s = payload.start_date != null ? String(payload.start_date).trim() : '';
    if (s) body.start_date = s;
    return this.post('/api/monthly-closings', body);
  }

  async deleteMonthlyClosing(id) {
    return this.delete(`/api/monthly-closings/${id}`);
  }

  async getMonthlyClosingExportData(id) {
    return this.get(`/api/monthly-closings/${id}/export-data`);
  }

  async getProductByBarcode(barcode) {
    return this.get(`/api/products/by-barcode/${encodeURIComponent(barcode)}`);
  }

  async searchProducts(q) {
    if (!q?.trim()) return { data: [] };
    return this.get(`/api/products/search?q=${encodeURIComponent(q.trim())}`);
  }

  // Audit logs
  async getAuditLogs(params = {}) {
    const filtered = Object.fromEntries(
      Object.entries(params).filter(([, v]) => v != null && v !== '')
    );
    const sp = new URLSearchParams(filtered).toString();
    return this.get(`/api/audit${sp ? '?' + sp : ''}`);
  }

  // Health
  async checkHealth() {
    try {
      const res = await this.get('/api/health');
      return res.status === 'ok';
    } catch {
      return false;
    }
  }
}

const apiService = new APIService();
export default apiService;
